import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSite } from '../context/SiteContext';
import Skeleton from 'react-loading-skeleton';

import wayalogo from '../assets/newlog.png';
import wayabankWhite from '../assets/svgs/wayabank-white.svg';

function TopNav() {
  const { settings, loading } = useSite();

  const [isScrolled, setIsScrolled] = useState(false);
  const [isProductsOpen, setIsProductsOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 40);
    };

    window.addEventListener('scroll', handleScroll);

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (window.innerWidth < 992) {
      document.body.style.overflow = isMenuOpen ? 'hidden' : 'auto';
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isMenuOpen]);

  const openDownloadPopup = (e) => {
    e.preventDefault();
    setIsMenuOpen(false);

    window.dispatchEvent(
      new CustomEvent('waya-open-download-popup')
    );
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
    setIsProductsOpen(false);
  };

  const navTextColor =
    isScrolled || isMenuOpen ? '#ffffff' : '#101828';

  const productSolutions = [
    {
      title: 'Personal Banking',
      desc: 'Open accounts, transfer money, pay bills, save and borrow easily.',
      items: [
        'Instant Transfers',
        'Bill Payments',
        'Loans',
        'ATM Card',
      ],
      link: '/personal-banking',
    },

    {
      title: 'Business Banking',
      desc: 'Built for SMEs, merchants and growing businesses.',
      items: [
        'Bulk Transfers',
        'Business Loans',
        'POS Services',
        'Invoicing',
      ],
      link: '/business-banking',
    },

    {
      title: 'USSD Banking',
      desc: 'Bank without internet from any mobile phone in Nigeria.',
      items: [
        'Offline Banking',
        'Transfers',
        'Airtime',
        'Bill Payments',
      ],
      link: '/ussd',
    },

    {
      title: 'POS & Merchant',
      desc: 'Accept payments anywhere with WayaBank POS terminals.',
      items: [
        'Merchant Payments',
        'Transaction Tracking',
        'Settlement',
        'POS Dashboard',
      ],
      link: '/merchant',
    },
  ];

  return (
    <>
      <nav
        className={`waya-navbar ${isScrolled ? 'scrolled' : ''}`}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          zIndex: 9999,
          transition: '0.3s ease',
          background: isScrolled
            ? 'rgba(255, 68, 0, 0.96)'
            : 'transparent',
          backdropFilter: isScrolled ? 'blur(14px)' : 'none',
          borderBottom: isScrolled
            ? '1px solid rgba(255, 255, 255, 0.12)'
            : '1px solid transparent',
        }}
      >
        <div
          style={{
            maxWidth: '1320px',
            margin: '0 auto',
            padding: '16px 5%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '20px',
          }}
        >
          {/* LOGO */}
          <a
            href="/"
            style={{
              display: 'flex',
              alignItems: 'center',
              textDecoration: 'none',
              zIndex: 10001,
            }}
          >
            {loading ? (
              <Skeleton width={120} height={32} />
            ) : (
              <img
                src={
                  isScrolled
                    ? wayabankWhite
                    : settings?.logo_url || wayalogo
                }
                alt="WayaBank"
                style={{
                  height: '34px',
                  width: 'auto',
                  objectFit: 'contain',
                }}
                onError={(e) => {
                  e.target.src = isScrolled
                    ? wayabankWhite
                    : wayalogo;
                }}
              />
            )}
          </a>

          {/* DESKTOP NAV */}
          <div className="waya-desktop-nav">
            <ul
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '34px',
                listStyle: 'none',
                margin: 0,
                padding: 0,
              }}
            >
              {[
                { name: 'HOME', path: '/' },
                { name: 'ABOUT', path: '/about' },
                { name: 'CONTACT', path: '/contact' },
              ].map((item) => (
                <li key={item.name}>
                  <a
                    href={item.path}
                    style={{
                      textDecoration: 'none',
                      color: navTextColor,
                      fontSize: '13px',
                      fontWeight:
                        location.pathname === item.path
                          ? '800'
                          : '700',
                      letterSpacing: '0.04em',
                      transition: '0.25s ease',
                      opacity:
                        location.pathname === item.path
                          ? 1
                          : 0.88,
                    }}
                  >
                    {item.name}
                  </a>
                </li>
              ))}

              {/* PRODUCTS */}
              <li
                style={{ position: 'relative' }}
                onMouseEnter={() => setIsProductsOpen(true)}
                onMouseLeave={() => setIsProductsOpen(false)}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    cursor: 'pointer',
                    color: navTextColor,
                    fontSize: '13px',
                    fontWeight: '700',
                    letterSpacing: '0.04em',
                    height: '40px',
                  }}
                >
                  PRODUCTS

                  <svg
                    width="13"
                    height="13"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{
                      transition: '0.25s ease',
                      transform: isProductsOpen
                        ? 'rotate(180deg)'
                        : 'rotate(0deg)',
                    }}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>

                {/* MEGA MENU */}
                {isProductsOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '155%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '760px',
                      background: '#ffffff',
                      borderRadius: '26px',
                      padding: '18px',
                      boxShadow:
                        '0 30px 90px rgba(0,0,0,0.12)',
                      border:
                        '1px solid rgba(0,0,0,0.05)',
                      zIndex: 999,
                    }}
                  >
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '12px',
                      }}
                    >
                      {productSolutions.map((item, index) => (
                        <a
                          key={index}
                          href={item.link}
                          style={{
                            textDecoration: 'none',
                            padding: '18px',
                            borderRadius: '18px',
                            transition: '0.25s ease',
                            border:
                              '1px solid transparent',
                            background: '#fff',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background =
                              '#f8fafc';

                            e.currentTarget.style.border =
                              '1px solid rgba(255, 68, 0, 0.08)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background =
                              '#fff';

                            e.currentTarget.style.border =
                              '1px solid transparent';
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '14px',
                            }}
                          >
                            {/* ICON */}
                            <div
                              style={{
                                width: '46px',
                                height: '46px',
                                minWidth: '46px',
                                borderRadius: '14px',
                                background:
                                  'rgba(255, 68, 0, 0.08)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="var(--waya-primary)"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <rect
                                  x="3"
                                  y="4"
                                  width="18"
                                  height="16"
                                  rx="2"
                                />

                                <line
                                  x1="7"
                                  y1="8"
                                  x2="17"
                                  y2="8"
                                />

                                <line
                                  x1="7"
                                  y1="12"
                                  x2="13"
                                  y2="12"
                                />
                              </svg>
                            </div>

                            {/* CONTENT */}
                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  fontSize: '15px',
                                  fontWeight: '800',
                                  color: '#111827',
                                  marginBottom: '5px',
                                  lineHeight: 1.2,
                                }}
                              >
                                {item.title}
                              </div>

                              <div
                                style={{
                                  fontSize: '12px',
                                  color: '#667085',
                                  lineHeight: '1.65',
                                  marginBottom: '14px',
                                  maxWidth: '260px',
                                }}
                              >
                                {item.desc}
                              </div>

                              {/* FEATURES */}
                              <div
                                style={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: '8px',
                                }}
                              >
                                {item.items.map(
                                  (feature, idx) => (
                                    <div
                                      key={idx}
                                      style={{
                                        padding:
                                          '6px 10px',
                                        borderRadius:
                                          '999px',
                                        background:
                                          '#f8fafc',
                                        fontSize:
                                          '11px',
                                        fontWeight:
                                          '600',
                                        color:
                                          '#475467',
                                      }}
                                    >
                                      {feature}
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                          </div>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </li>

              <li>
                <a
                  href="https://www.wayalinks.com/blogs/#wayabank"
                  style={{
                    textDecoration: 'none',
                    color: navTextColor,
                    fontSize: '13px',
                    fontWeight: '700',
                    letterSpacing: '0.04em',
                    opacity: 0.88,
                  }}
                >
                  BLOG
                </a>
              </li>
            </ul>
          </div>

          {/* RIGHT */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '14px',
            }}
          >
            {/* SIGN UP */}
            <button
              onClick={openDownloadPopup}
              className="desktop-signup-btn"
              style={{
                background: '#ffffff',
                color: 'var(--waya-primary)',
                border: isScrolled
                  ? '1px solid rgba(255,255,255,0.2)'
                  : '2px solid var(--waya-primary)',
                padding: '12px 24px',
                borderRadius: '13px',
                fontSize: '13px',
                fontWeight: '800',
                letterSpacing: '0.03em',
                cursor: 'pointer',
                transition: '0.25s ease',
                boxShadow: '0 10px 24px rgba(255, 68, 0, 0.25)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform =
                  'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform =
                  'translateY(0)';
              }}
            >
              CREATE ACCOUNT
            </button>

            {/* MOBILE TOGGLE */}
            <button
              onClick={() =>
                setIsMenuOpen(!isMenuOpen)
              }
              className="waya-mobile-toggle"
              style={{
                background: 'transparent',
                border: 'none',
                outline: 'none',
                display: 'none',
                zIndex: 10001,
                padding: 0,
              }}
            >
              <svg
                width="26"
                height="26"
                viewBox="0 0 24 24"
                fill="none"
                stroke={navTextColor}
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                {isMenuOpen ? (
                  <>
                    <line
                      x1="18"
                      y1="6"
                      x2="6"
                      y2="18"
                    />

                    <line
                      x1="6"
                      y1="6"
                      x2="18"
                      y2="18"
                    />
                  </>
                ) : (
                  <>
                    <line
                      x1="3"
                      y1="6"
                      x2="21"
                      y2="6"
                    />

                    <line
                      x1="3"
                      y1="12"
                      x2="21"
                      y2="12"
                    />

                    <line
                      x1="3"
                      y1="18"
                      x2="21"
                      y2="18"
                    />
                  </>
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* MOBILE MENU */}
        <div
          className={`waya-mobile-menu ${isMenuOpen ? 'open' : ''
            }`}
          style={{
            position: 'fixed',
            top: 0,
            right: isMenuOpen ? '0' : '-100%',
            width: '100%',
            height: '100vh',
            background: '#ff4400',
            transition: '0.35s ease',
            zIndex: 9998,
            padding: '110px 30px 40px',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '32px',
            }}
          >
            {[
              { name: 'HOME', path: '/' },
              { name: 'ABOUT', path: '/about' },
              { name: 'BLOG', path: '/blogs' },
              { name: 'CONTACT', path: '/contact' },
            ].map((item) => (
              <a
                key={item.name}
                href={item.path}
                onClick={closeMenu}
                style={{
                  textDecoration: 'none',
                  color: '#fff',
                  fontSize: '22px',
                  fontWeight: '800',
                  letterSpacing: '0.04em',
                }}
              >
                {item.name}
              </a>
            ))}

            {/* MOBILE PRODUCTS */}
            <div
              style={{
                marginTop: '8px',
                paddingTop: '24px',
                borderTop:
                  '1px solid rgba(255,255,255,0.22)',
              }}
            >
              <div
                style={{
                  color: 'rgba(255,255,255,0.72)',
                  fontSize: '11px',
                  fontWeight: '700',
                  marginBottom: '18px',
                  letterSpacing: '0.08em',
                }}
              >
                PRODUCTS
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '16px',
                }}
              >
                {productSolutions.map(
                  (item, index) => (
                    <a
                      key={index}
                      href={item.link}
                      onClick={closeMenu}
                      style={{
                        textDecoration: 'none',
                        color: '#fff',
                      }}
                    >
                      <div
                        style={{
                          fontSize: '14px',
                          fontWeight: '700',
                          marginBottom: '4px',
                        }}
                      >
                        {item.title}
                      </div>

                      <div
                        style={{
                          fontSize: '12px',
                          lineHeight: '1.6',
                          color:
                            'rgba(255,255,255,0.72)',
                        }}
                      >
                        {item.desc}
                      </div>
                    </a>
                  )
                )}
              </div>
            </div>

            <button
              onClick={openDownloadPopup}
              style={{
                marginTop: '30px',
                background: '#fff',
                border: 'none',
                color: 'var(--waya-primary)',
                height: '52px',
                borderRadius: '14px',
                fontSize: '14px',
                fontWeight: '800',
                letterSpacing: '0.03em',
                cursor: 'pointer',
              }}
            >
              CREATE ACCOUNT
            </button>
          </div>
        </div>
      </nav>

      <style>
        {`
          @media (max-width: 991px) {
            .waya-desktop-nav {
              display: none !important;
            }

            .desktop-signup-btn {
              display: none !important;
            }

            .waya-mobile-toggle {
              display: flex !important;
              align-items: center;
              justify-content: center;
            }
          }

          @media (min-width: 992px) {
            .waya-mobile-menu {
              display: none !important;
            }
          }

          @media (max-width: 1200px) {
            .waya-mega-menu {
              width: 680px !important;
            }
          }

          @media (max-width: 991px) {
            .waya-mega-menu {
              display: none !important;
            }
          }
        `}
      </style>
    </>
  );
}

export default TopNav;
